import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {useToast} from './Toast';
import {apiErrorMessage} from '../lib/apiError';
import {
  adminWordlistCreateApiSelector,
  adminWordlistsApiSelector,
  adminWordlistsPageSelector,
  adminWordlistsSelector,
  adminWordlistsTotalSelector,
} from '../store/selectors/admin.selectors';
import {createAdminWordlist, fetchAdminWordlists} from '../store/slices/adminWordlistsThunks';
import {ADMIN_WORDLISTS_PAGE_SIZE, AdminTriState} from '../domain/adminWordlists';
import AdminWordlistFilters, {AdminWordlistFilterValues} from './admin/AdminWordlistFilters';
import AdminWordlistsTable from './admin/AdminWordlistsTable';
import AdminWordlistFormModal, {AdminWordlistCreateSubmission} from './admin/AdminWordlistFormModal';
import {Pagination, Panel} from './admin/AdminPrimitives';
import {PlusIcon} from './Icons';

/**
 * The word list catalog's logic: filters, paging, the fetch, and creating a new list.
 *
 * The query lives in the URL, same reasoning as `adminUsers.container.tsx` — an admin screen is one
 * people paste at each other, and a filtered list that survives a reload is worth the small amount
 * of parsing. The search box is the exception: it holds local state, debounced into the URL.
 */

const SEARCH_DEBOUNCE_MS = 300;

const AdminWordlistsContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {showToast} = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const wordlists = useAppSelector(adminWordlistsSelector);
  const total = useAppSelector(adminWordlistsTotalSelector);
  const page = useAppSelector(adminWordlistsPageSelector);
  const api = useAppSelector(adminWordlistsApiSelector);
  const createApi = useAppSelector(adminWordlistCreateApiSelector);

  const search = searchParams.get('search') ?? '';
  const officialParam = searchParams.get('is_official');
  const isOfficial: AdminTriState = officialParam === 'true' || officialParam === 'false' ? officialParam : '';
  const publicParam = searchParams.get('is_public');
  const isPublic: AdminTriState = publicParam === 'true' || publicParam === 'false' ? publicParam : '';
  const includeDeleted = searchParams.get('include_deleted') === 'true';
  const offset = Math.max(0, Number(searchParams.get('offset') ?? 0) || 0);

  const [searchInput, setSearchInput] = useState(search);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // Keep the box in step when the URL changes from somewhere else (the back button, a shared link).
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const updateParams = useCallback(
    (patch: Record<string, string | undefined>, options?: {keepOffset?: boolean}) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);

          Object.entries(patch).forEach(([key, value]) => {
            if (value === undefined || value === '') {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          });

          // Any change to what is being listed puts you back on page one.
          if (!options?.keepOffset) {
            next.delete('offset');
          }

          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (searchInput === search) {
      return;
    }

    const timer = window.setTimeout(() => {
      updateParams({search: searchInput || undefined});
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchInput, search, updateParams]);

  const query = useMemo(
    () => ({
      search: search || undefined,
      isOfficial: isOfficial || undefined,
      isPublic: isPublic || undefined,
      includeDeleted: includeDeleted || undefined,
      limit: ADMIN_WORDLISTS_PAGE_SIZE,
      offset,
    }),
    [search, isOfficial, isPublic, includeDeleted, offset]
  );

  useEffect(() => {
    dispatch(fetchAdminWordlists(query));
  }, [dispatch, query]);

  const values: AdminWordlistFilterValues = useMemo(
    () => ({search, isOfficial, isPublic, includeDeleted}),
    [search, isOfficial, isPublic, includeDeleted]
  );

  const hasFilters = Boolean(search || isOfficial || isPublic || includeDeleted);

  const handleCreate = async (submission: AdminWordlistCreateSubmission) => {
    try {
      const created = await dispatch(createAdminWordlist({
        name: submission.name,
        description: submission.description || undefined,
        words: submission.words,
        isPublic: submission.isPublic,
        isOfficial: submission.isOfficial,
        userId: submission.userId || undefined,
      })).unwrap();

      showToast(`Created “${created.name}”`, 'success');
      setIsCreateOpen(false);
      navigate(`/admin/wordlists/${created.id}`);
    } catch (error) {
      showToast(apiErrorMessage(error, 'Could not create the word list'), 'error');
    }
  };

  return (
    <>
      <Panel
        title="Word lists"
        subtitle="Every list across every user. Deleting here is a soft delete — restore it from its page."
        padded={false}
        actions={
          <button
            type="button"
            onClick={() => setIsCreateOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-2.5 py-1.5
             text-[11px] font-medium text-white transition-colors hover:bg-indigo-500 cursor-pointer"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            New word list
          </button>
        }
      >
        <div className="border-b border-gray-800 px-4 py-3">
          <AdminWordlistFilters
            values={values}
            searchInput={searchInput}
            onSearchInput={setSearchInput}
            onChange={(patch) => {
              const next: Record<string, string | undefined> = {};
              if ('isOfficial' in patch) {
                next.is_official = patch.isOfficial || undefined;
              }
              if ('isPublic' in patch) {
                next.is_public = patch.isPublic || undefined;
              }
              if ('includeDeleted' in patch) {
                next.include_deleted = patch.includeDeleted ? 'true' : undefined;
              }
              updateParams(next);
            }}
            onClear={() => updateParams({
              search: undefined,
              is_official: undefined,
              is_public: undefined,
              include_deleted: undefined,
            })}
            resultCount={total}
            isLoading={api.status === 'pending'}
          />
        </div>

        <AdminWordlistsTable
          wordlists={wordlists}
          isLoading={api.status === 'pending'}
          error={api.status === 'rejected' ? api.error : null}
          onRetry={() => dispatch(fetchAdminWordlists(query))}
          hasFilters={hasFilters}
        />

        <Pagination
          total={total}
          limit={page?.limit || ADMIN_WORDLISTS_PAGE_SIZE}
          offset={offset}
          onOffsetChange={next => updateParams({offset: String(next)}, {keepOffset: true})}
          noun="word lists"
          isLoading={api.status === 'pending'}
        />
      </Panel>

      <AdminWordlistFormModal
        isOpen={isCreateOpen}
        isSaving={createApi.status === 'pending'}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={handleCreate}
      />
    </>
  );
};

export default AdminWordlistsContainer;
